import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookingWizard } from '@/components/booking/BookingWizard';
import { verifyBooking, getBundledServices, getStaffBookingLocations } from '@/services/bookingApi';
import apiClient from '@/lib/api-client';
import type { PublicProfile, Service, ServiceCategory } from '@/types';
import type { BookingLocationOption } from '@/components/booking/LocationPicker';

export interface BookingFlowPageProps {
  entryMode?: 'location' | 'staff';
  variant?: 'page' | 'embed';
}

const BookingFlowPage: React.FC<BookingFlowPageProps> = ({
  entryMode = 'location',
  variant = 'page',
}) => {
  const { t } = useTranslation('public');
  const { username, userId } = useParams<{ username?: string; userId?: string }>();
  const [searchParams] = useSearchParams();

  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [locationOptions, setLocationOptions] = useState<BookingLocationOption[]>([]);
  const [fixedStaffId, setFixedStaffId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<
    'idle' | 'verifying' | 'verified' | 'failed'
  >('idle');

  const slug = username || profile?.slug;

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (entryMode === 'staff' && userId) {
          setFixedStaffId(userId);
          const locations = await getStaffBookingLocations(userId);
          setLocationOptions(locations);

          const staffParam = searchParams.get('location');
          const initialLoc =
            locations.find((l) => l.slug === staffParam || l.id === staffParam) ??
            (locations.length === 1 ? locations[0] : null);

          if (!initialLoc && locations.length > 1) {
            setProfile(null);
            setServices([]);
            setIsLoading(false);
            return;
          }

          if (!initialLoc) {
            setError('No booking locations available for this staff member.');
            setIsLoading(false);
            return;
          }

          const profileResponse = await apiClient.get<{ data: PublicProfile }>(
            `/profiles/${initialLoc.slug}`
          );
          const bundled = await getBundledServices(initialLoc.slug).catch(() => [] as Service[]);
          setProfile(profileResponse.data.data);
          setServices(bundled.length > 0 ? bundled : profileResponse.data.data.services || []);
          buildCategories(bundled.length > 0 ? bundled : profileResponse.data.data.services || [], profileResponse.data.data);
          return;
        }

        if (!username) return;

        const [profileResponse, bundledServices] = await Promise.all([
          apiClient.get<{ data: PublicProfile }>(`/profiles/${username}`),
          getBundledServices(username).catch(() => [] as Service[]),
        ]);

        setProfile(profileResponse.data.data);
        const allServices =
          bundledServices.length > 0
            ? bundledServices
            : profileResponse.data.data.services || [];
        setServices(allServices);
        buildCategories(allServices, profileResponse.data.data);

        const staffParam = searchParams.get('staff');
        if (staffParam) setFixedStaffId(staffParam);
      } catch (err: unknown) {
        setError(
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to load profile'
        );
      } finally {
        setIsLoading(false);
      }
    };

    const buildCategories = (allServices: Service[], prof: PublicProfile) => {
      const categoryMap = new Map<string, ServiceCategory>();
      allServices.forEach((service: Service) => {
        if (service.category_id && service.category) {
          categoryMap.set(service.category_id, service.category);
        }
      });
      prof.categories?.forEach((category) => {
        categoryMap.set(category.id, category);
      });
      setCategories(Array.from(categoryMap.values()));
    };

    load();
  }, [username, userId, entryMode, searchParams]);

  useEffect(() => {
    const verifyParam = searchParams.get('verify');
    if (verifyParam) {
      setVerificationStatus('verifying');
      verifyBooking(verifyParam)
        .then(() => setVerificationStatus('verified'))
        .catch(() => setVerificationStatus('failed'));
    }
  }, [searchParams]);

  const defaultCurrency = profile?.currency || 'EUR';
  const supportedCurrencies = useMemo(() => {
    if (profile?.supported_currencies?.length) return profile.supported_currencies;
    return [defaultCurrency];
  }, [profile, defaultCurrency]);

  const verificationMethod =
    profile?.booking_settings?.client_verification_method || 'email';
  const clientVerificationEnabled =
    profile?.booking_settings?.client_verification_enabled ?? true;

  const shellMin = variant === 'embed' ? 'min-h-0' : 'min-h-screen';

  if (isLoading) {
    return (
      <div className={`${shellMin} bg-gray-50 flex items-center justify-center`}>
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (entryMode === 'staff' && locationOptions.length > 1 && !profile) {
    return (
      <div className={`${shellMin} bg-gray-50 p-4`}>
        <BookingWizard
          profileId={locationOptions[0]?.id || ''}
          username={locationOptions[0]?.slug || ''}
          services={[]}
          categories={[]}
          entryMode="staff"
          fixedStaffId={fixedStaffId}
          locationOptions={locationOptions}
          variant={variant}
          onComplete={() => window.history.back()}
          onCancel={() => window.history.back()}
        />
      </div>
    );
  }

  if (error || !profile || !slug) {
    return (
      <div className={`${shellMin} bg-gray-50 flex items-center justify-center`}>
        <p className="text-gray-600">{error || t('booking.notFound')}</p>
      </div>
    );
  }

  if (verificationStatus === 'verifying' || verificationStatus === 'verified') {
    return (
      <div className={`${shellMin} bg-gray-50 flex items-center justify-center`}>
        <p>{verificationStatus === 'verified' ? t('booking.verified') : t('booking.verifying')}</p>
      </div>
    );
  }

  return (
    <div className={variant === 'embed' ? '' : shellMin + ' bg-gray-50'}>
      {variant !== 'embed' && (
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <a href={`/${slug}`} className="text-gray-500 hover:text-gray-700 text-sm">
              {t('booking.backToProfile', {
                business_name: profile.business_name,
                username: slug,
              })}
            </a>
          </div>
        </div>
      )}

      <BookingWizard
        profileId={profile.id}
        username={slug}
        profileTimezone={profile.timezone}
        services={services}
        categories={categories}
        colorScheme={profile.color_scheme}
        typography={profile.typography}
        defaultCurrency={defaultCurrency}
        supportedCurrencies={supportedCurrencies}
        verificationMethod={verificationMethod}
        clientVerificationEnabled={clientVerificationEnabled}
        entryMode={entryMode}
        fixedStaffId={fixedStaffId}
        locationOptions={locationOptions}
        variant={variant}
        allowCustomerStaffSelection={
          profile.booking_settings?.allow_customer_staff_selection ?? true
        }
        onComplete={() => window.history.back()}
        onCancel={() => window.history.back()}
      />
    </div>
  );
};

export default BookingFlowPage;
